import { useState, useEffect } from "react";
import { Copy, Check, Loader2, AlertCircle, Building2, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button } from "@/components/ui";
import { companyApi } from "@/services/api";

interface DepositInfo {
  bankName: string;
  bankCode: string;
  agency: string;
  accountNumber: string;
  accountDigit?: string;
  pixKey?: string;
  holderName?: string;
  holderDocument?: string;
}

interface DepositModalProps {
  open: boolean;
  onClose: () => void;
}

export function DepositModal({ open, onClose }: DepositModalProps) {
  const [depositInfo, setDepositInfo] = useState<DepositInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadDepositInfo();
    }
  }, [open]);

  const loadDepositInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const info = await companyApi.dashboard.getDepositInfo();
      setDepositInfo(info);
    } catch (err: any) {
      setError(err.message || "Erro ao carregar dados de depósito");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Fallback
    }
  };

  const formatDocument = (doc?: string) => {
    if (!doc) return "";
    const cleaned = doc.replace(/\D/g, "");
    if (cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    return doc;
  };

  const CopyButton = ({ text, field }: { text: string; field: string }) => (
    <button
      onClick={() => handleCopy(text, field)}
      className="p-1.5 hover:bg-muted rounded transition-colors"
      title="Copiar"
    >
      {copiedField === field ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <Copy className="w-4 h-4 text-muted-foreground" />
      )}
    </button>
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            <CardTitle>Depositar via PIX</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Faça um PIX para a conta abaixo. Seu saldo será creditado automaticamente.
          </p>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {depositInfo && !loading && (
            <div className="space-y-4">
              {/* Dados Bancários */}
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-medium">Dados Bancários</h3>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Banco</p>
                    <p className="font-medium">{depositInfo.bankName} ({depositInfo.bankCode})</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Agência</p>
                    <div className="flex items-center gap-1">
                      <p className="font-medium">{depositInfo.agency}</p>
                      <CopyButton text={depositInfo.agency} field="agency" />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Conta Corrente</p>
                    <div className="flex items-center gap-1">
                      <p className="font-medium">
                        {depositInfo.accountNumber}
                        {depositInfo.accountDigit && `-${depositInfo.accountDigit}`}
                      </p>
                      <CopyButton
                        text={`${depositInfo.accountNumber}${depositInfo.accountDigit ? `-${depositInfo.accountDigit}` : ""}`}
                        field="account"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Titular */}
              {depositInfo.holderName && (
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <h3 className="text-sm font-medium">Titular</h3>
                  <div className="text-sm">
                    <p className="font-medium">{depositInfo.holderName}</p>
                    {depositInfo.holderDocument && (
                      <p className="text-muted-foreground">CNPJ: {formatDocument(depositInfo.holderDocument)}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Chave PIX */}
              {depositInfo.pixKey && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-primary mb-2">Chave PIX</h3>
                  <div className="flex items-center justify-between bg-muted rounded px-3 py-2">
                    <span className="font-mono text-sm truncate">{depositInfo.pixKey}</span>
                    <button
                      onClick={() => handleCopy(depositInfo.pixKey!, "pix")}
                      className="ml-2 flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium rounded transition-colors"
                    >
                      {copiedField === "pix" ? (
                        <>
                          <Check className="w-3 h-3" />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          Copiar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Aviso */}
              <p className="text-xs text-muted-foreground text-center">
                Após a confirmação do PIX, seu saldo em BRLD será creditado automaticamente.
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
